import { memo } from "react";
import { ArrowDownRight, ArrowUpRight, Minus } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { getChartSeriesColor } from "@/constants/chartColors";
import {
  formatGscCount,
  formatGscCtr,
  formatGscPosition,
} from "@/lib/gsc-display";
import { cn } from "@/lib/utils";
import {
  GSC_DEFAULT_COMPARE_METRIC,
  type GscCompareDomainSummary,
  type GscCompareMetric,
} from "@/types/gsc";

export interface CompareMetricsCardsProps {
  data?: readonly GscCompareDomainSummary[];
  selectedDomains?: readonly string[];
  availableDomains?: readonly string[];
  activeMetric?: GscCompareMetric;
  isLoading?: boolean;
  className?: string;
}

const DEFAULT_SKELETON_CARD_COUNT = 3;
const FLAT_DELTA_THRESHOLD = 0.01;

const METRIC_CONFIG = {
  clicks: {
    key: "clicks",
    prevKey: "prev_clicks",
    label: "Clicks",
    format: formatGscCount,
    lowerIsBetter: false,
  },
  impressions: {
    key: "impressions",
    prevKey: "prev_impressions",
    label: "Impressions",
    format: formatGscCount,
    lowerIsBetter: false,
  },
  ctr: {
    key: "ctr",
    prevKey: "prev_ctr",
    label: "CTR",
    format: formatGscCtr,
    lowerIsBetter: false,
  },
  position: {
    key: "avg_position",
    prevKey: "prev_avg_position",
    label: "Avg Position",
    format: formatGscPosition,
    lowerIsBetter: true,
  },
} as const satisfies Record<
  GscCompareMetric,
  {
    key: keyof Pick<
      GscCompareDomainSummary,
      "clicks" | "impressions" | "ctr" | "avg_position"
    >;
    prevKey: keyof Pick<
      GscCompareDomainSummary,
      | "prev_clicks"
      | "prev_impressions"
      | "prev_ctr"
      | "prev_avg_position"
    >;
    label: string;
    format: (value: number) => string;
    lowerIsBetter: boolean;
  }
>;

const createZeroCompareDomainSummary = (
  domain: string,
): GscCompareDomainSummary => ({
  domain,
  clicks: 0,
  impressions: 0,
  ctr: 0,
  avg_position: 0,
  prev_clicks: 0,
  prev_impressions: 0,
  prev_ctr: 0,
  prev_avg_position: 0,
});

const SECONDARY_METRICS = [
  {
    key: "clicks",
    label: "Clicks",
    format: formatGscCount,
  },
  {
    key: "impressions",
    label: "Impressions",
    format: formatGscCount,
  },
  {
    key: "ctr",
    label: "CTR",
    format: formatGscCtr,
  },
  {
    key: "avg_position",
    label: "Avg Position",
    format: formatGscPosition,
  },
] as const satisfies ReadonlyArray<{
  key: keyof Pick<
    GscCompareDomainSummary,
    "clicks" | "impressions" | "ctr" | "avg_position"
  >;
  label: string;
  format: (value: number) => string;
}>;

const domainAccentColor = (
  domain: string,
  availableDomains: readonly string[],
): string => {
  const index = availableDomains.indexOf(domain);
  return getChartSeriesColor(index >= 0 ? index : 0);
};

const calcMetricDeltaPercent = (
  current: number,
  previous: number,
): number | null => {
  if (!Number.isFinite(current) || !Number.isFinite(previous)) return null;
  if (previous === 0) {
    if (current === 0) return 0;
    if (current > 0) return 100;
    return null;
  }
  const delta = ((current - previous) / previous) * 100;
  return Number.isFinite(delta) ? delta : null;
};

const formatDeltaPercent = (deltaPercent: number, isFlat: boolean): string => {
  if (isFlat) return "0.00%";
  const abs = Math.abs(deltaPercent).toFixed(2);
  return deltaPercent > 0 ? `+${abs}%` : `-${abs}%`;
};

interface MetricDeltaProps {
  current: number;
  previous: number;
  lowerIsBetter?: boolean;
}

const MetricDelta = ({
  current,
  previous,
  lowerIsBetter = false,
}: MetricDeltaProps) => {
  const deltaPercent = calcMetricDeltaPercent(current, previous);
  if (deltaPercent === null) return null;

  const isFlat = Math.abs(deltaPercent) < FLAT_DELTA_THRESHOLD;
  const visuallyPositive = lowerIsBetter
    ? deltaPercent < 0
    : deltaPercent > 0;
  const Icon = isFlat
    ? Minus
    : deltaPercent > 0
      ? ArrowUpRight
      : ArrowDownRight;

  return (
    <div
      className={cn(
        "flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-sm",
        isFlat
          ? "text-muted-foreground"
          : visuallyPositive
            ? "signal-text-positive"
            : "signal-text-negative",
      )}
      aria-label={`${formatDeltaPercent(deltaPercent, isFlat)} vs prev period`}
    >
      <Icon className="size-4 shrink-0" aria-hidden />
      <span className="font-medium tabular-nums">
        {formatDeltaPercent(deltaPercent, isFlat)}
      </span>
      <span className="font-normal text-muted-foreground">vs prev period</span>
    </div>
  );
};

const CompareMetricsCardSkeleton = () => (
  <Card className="border-border border-t-4 border-t-muted shadow-none">
    <CardHeader className="space-y-0 pb-3">
      <div className="flex items-center gap-2">
        <Skeleton className="size-2 rounded-full" />
        <Skeleton className="h-4 w-36" />
      </div>
    </CardHeader>
    <CardContent className="space-y-4">
      <div className="space-y-2">
        <Skeleton className="h-3 w-14" />
        <Skeleton className="h-9 w-28" />
        <Skeleton className="h-4 w-40" />
      </div>
      <div className="space-y-2 border-t border-border pt-3">
        {Array.from({ length: 4 }, (_, index) => (
          <div key={index} className="flex items-center justify-between gap-3">
            <Skeleton className="h-3 w-20" />
            <Skeleton className="h-3 w-14" />
          </div>
        ))}
      </div>
    </CardContent>
  </Card>
);

interface CompareMetricsCardProps {
  row: GscCompareDomainSummary;
  availableDomains: readonly string[];
  activeMetric: GscCompareMetric;
}

const CompareMetricsCard = memo(function CompareMetricsCard({
  row,
  availableDomains,
  activeMetric,
}: CompareMetricsCardProps) {
  const accent = domainAccentColor(row.domain, availableDomains);
  const metric = METRIC_CONFIG[activeMetric];
  const current = row[metric.key];
  const previous = row[metric.prevKey];

  return (
    <Card
      className="border-border border-t-4 shadow-none"
      style={{ borderTopColor: accent }}
    >
      <CardHeader className="space-y-0 pb-3">
        <CardTitle className="flex items-center gap-2 text-sm font-medium tracking-tight">
          <span
            className="size-2 shrink-0 rounded-full"
            style={{ backgroundColor: accent }}
            aria-hidden
          />
          <span className="truncate">{row.domain}</span>
        </CardTitle>
      </CardHeader>

      <CardContent className="space-y-4">
        <div className="space-y-1.5">
          <p className="text-xs font-bold tracking-wider text-muted-foreground uppercase">
            {metric.label}
          </p>
          <p className="text-3xl font-bold tracking-tight text-foreground tabular-nums">
            {metric.format(current)}
          </p>
          <MetricDelta
            current={current}
            previous={previous}
            lowerIsBetter={metric.lowerIsBetter}
          />
        </div>

        <dl className="space-y-2 border-t border-border pt-3">
          {SECONDARY_METRICS.map((item) => (
            <div
              key={item.key}
              className="flex items-center justify-between gap-3 text-sm"
            >
              <dt className="text-muted-foreground">{item.label}</dt>
              <dd className="font-medium text-foreground tabular-nums">
                {item.format(row[item.key])}
              </dd>
            </div>
          ))}
        </dl>
      </CardContent>
    </Card>
  );
});

function CompareMetricsCardsComponent({
  data = [],
  selectedDomains = [],
  availableDomains = [],
  activeMetric = GSC_DEFAULT_COMPARE_METRIC,
  isLoading = false,
  className,
}: CompareMetricsCardsProps) {
  const safeData = Array.isArray(data) ? data : [];
  const safeSelectedDomains = Array.isArray(selectedDomains)
    ? selectedDomains
    : [];
  const safeAvailableDomains = Array.isArray(availableDomains)
    ? availableDomains
    : [];

  if (isLoading) {
    const skeletonCount =
      safeSelectedDomains.length || DEFAULT_SKELETON_CARD_COUNT;

    return (
      <div
        className={cn(
          "grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3",
          className,
        )}
        aria-busy
        aria-label="Loading domain compare metrics"
      >
        {Array.from({ length: skeletonCount }, (_, index) => (
          <CompareMetricsCardSkeleton key={index} />
        ))}
      </div>
    );
  }

  if (safeSelectedDomains.length === 0) {
    return null;
  }

  const byDomain = new Map(
    safeData.map((row) => [row.domain, row] as const),
  );

  return (
    <div
      className={cn(
        "grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3",
        className,
      )}
      aria-label="Domain compare metrics"
    >
      {safeSelectedDomains.map((domain) => {
        const row =
          byDomain.get(domain) ?? createZeroCompareDomainSummary(domain);

        return (
          <CompareMetricsCard
            key={domain}
            row={row}
            availableDomains={safeAvailableDomains}
            activeMetric={activeMetric}
          />
        );
      })}
    </div>
  );
}

export const CompareMetricsCards = memo(CompareMetricsCardsComponent);
CompareMetricsCards.displayName = "CompareMetricsCards";
